// Contador de expiração da sessão (decisão do operador, 2026-08-10).
//
// Vive em ui-primitives, e não em cada SPA, porque a regra é UMA: os três apps
// autenticados (comitê, admin, recurso) usam o mesmo formato de token. Três
// cópias divergiriam a partir do primeiro dia — o padrão "a contagem de cópias
// de uma regra é parte do diagnóstico" do CLAUDE.md.
//
// ── POR QUE min(inactivity, absolute)
//
// O token carrega DUAS expirações (apps/api/src/services/jwt-session.ts:7-14):
//   expInactivity — janela DESLIZANTE de 30 min, renovada a cada requisição
//                   autenticada (o `apiFetch` de cada app aplica o header
//                   `X-Session-Refresh`, então o token em mãos é sempre o fresco);
//   expAbsolute   — teto DURO de 8h desde a emissão, JAMAIS renovado.
//
// Quem encerra a sessão é o que vencer PRIMEIRO. Mostrar só o deslizante mentiria
// na última meia hora de vida do token: o contador reiniciaria em 30:00 a cada
// clique enquanto o teto absoluto se aproxima, e a sessão cairia com o relógio
// marcando tempo de sobra. É o mesmo modo de falha do "fallback plausível":
// um número crível que não corresponde ao que vai acontecer.
//
// ── O QUE ESTE COMPONENTE NÃO FAZ
//
// Não desloga, não renova, não navega. Ele INFORMA. Sessão morta já tem
// tratamento próprio nos apps (needsReauth / borda sessao_expirada), e duplicar
// essa decisão aqui criaria duas fontes divergentes para o mesmo evento.

import { useEffect, useState } from 'react';
import './SessionCountdown.css';

export interface SessionCountdownProps {
  /** JWT da sessão. Quando renovado, o contador recalcula sozinho. */
  token: string | null | undefined;
  /** Rótulo — ex.: "Sessão expira em". O app traduz; o primitivo não tem i18n. */
  label: string;
  /** Texto quando já expirou. */
  expiredLabel: string;
  /** Abaixo de quantos segundos entra em destaque. Default: 5 min. */
  warnBelowSeconds?: number;
}

interface ExpClaims {
  expInactivity?: number;
  expAbsolute?: number;
  exp?: number;
}

/**
 * Lê as expirações do JWT SEM verificar assinatura — e isso é correto aqui:
 * quem valida é o servidor, a cada requisição. Este payload é usado só para
 * DESENHAR um contador. Um token adulterado no browser faria o contador mentir
 * para quem o adulterou, e nada mais: nenhuma decisão de acesso passa por aqui.
 */
export function lerExpiracaoSegundos(token: string | null | undefined): number | null {
  if (!token) return null;
  const partes = token.split('.');
  if (partes.length < 2) return null;
  try {
    // base64url → base64. `atob` não aceita '-'/'_' nem padding ausente.
    const b64 = partes[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const claims = JSON.parse(atob(b64 + pad)) as ExpClaims;
    const candidatos = [claims.expInactivity, claims.expAbsolute, claims.exp].filter(
      (v): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0,
    );
    if (candidatos.length === 0) return null;
    // O MENOR: é o que efetivamente encerra a sessão.
    return Math.min(...candidatos);
  } catch {
    // Token ilegível → sem contador. NÃO devolve 0: zero seria "expirou agora",
    // uma afirmação forte sobre a sessão que este componente não tem como fazer.
    return null;
  }
}

/**
 * Exportada para ser testável DIRETAMENTE. O `Math.max(0, …)` abaixo é defesa
 * REDUNDANTE: no fluxo do componente o ramo `expirou` já intercepta antes de
 * chegar aqui, então nenhum teste de tela consegue exercitá-lo — provado por
 * mutação (removê-lo deixava a suíte inteira verde). O CLAUDE.md manda fixar
 * cada defesa individualmente quando há redundância; sem export, esta ficaria
 * sem testemunha e a próxima pessoa concluiria que é código morto.
 */
export function formatarDuracao(segundos: number): string {
  const s = Math.max(0, Math.floor(segundos));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const seg = s % 60;
  const dois = (n: number): string => String(n).padStart(2, '0');
  // Horas só aparecem quando existem — "00:29:58" polui o cabeçalho no caso
  // comum, que é a janela de 30 min.
  return h > 0 ? `${dois(h)}:${dois(m)}:${dois(seg)}` : `${dois(m)}:${dois(seg)}`;
}

export function SessionCountdown({
  token,
  label,
  expiredLabel,
  warnBelowSeconds = 300,
}: SessionCountdownProps): JSX.Element | null {
  const expEpochSec = lerExpiracaoSegundos(token);
  const [agora, setAgora] = useState(() => Date.now());

  useEffect(() => {
    if (expEpochSec === null) return;
    // 1s fixo: o contador mostra segundos, então qualquer intervalo maior faria
    // saltar números. `setInterval` é limpo na troca de token e na desmontagem.
    const id = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expEpochSec]);

  // Sem token ou token ilegível: nada é renderizado. Um contador vazio ou
  // "--:--" sugeriria que a sessão tem prazo desconhecido, quando o caso real é
  // "não há sessão".
  if (expEpochSec === null) return null;

  const restante = expEpochSec - Math.floor(agora / 1000);
  const expirou = restante <= 0;
  const alerta = !expirou && restante <= warnBelowSeconds;

  return (
    <span
      className={
        'session-countdown' +
        (expirou ? ' session-countdown--expired' : alerta ? ' session-countdown--warn' : '')
      }
      data-testid="session-countdown"
      // `polite`, não `assertive`: o contador muda a cada segundo e um live
      // region agressivo faria o leitor de tela interromper a leitura sem parar.
      aria-live="polite"
      // Só o minuto entra no rótulo acessível — anunciar segundo a segundo é
      // ruído para quem usa leitor de tela.
      aria-label={expirou ? expiredLabel : `${label} ${Math.ceil(restante / 60)} min`}
    >
      {expirou ? expiredLabel : `${label} ${formatarDuracao(restante)}`}
    </span>
  );
}
